import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, Check, X, ArrowRight, Sparkles, GraduationCap, CalendarCheck, FileDown, Star } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Motion";
import Seo from "@/components/Seo";
import BookingDialog from "@/components/BookingDialog";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { track } from "@/lib/analytics";

const LEVELS = [{ n: 1, name: "Base", pts: 2 }, { n: 2, name: "Intermedio", pts: 3 }, { n: 3, name: "Avanzato", pts: 5 }];
const SESSION_SERVICE = "Sessione Tema Natale — Talenti e Doni";
const LETTERS = ["A", "B", "C", "D"];

function Locked() {
  return (
    <div className="max-w-2xl mx-auto text-center py-20" data-testid="quiz-locked">
      <div className="w-16 h-16 rounded-full border border-gold/40 flex items-center justify-center mx-auto mb-8 text-gold"><Lock size={22} /></div>
      <p className="text-xs uppercase tracking-[0.25em] text-gold mb-4">Quiz Spirituale · 30 domande</p>
      <h1 className="font-display text-4xl sm:text-5xl text-[#2A1F14] leading-tight mb-6">Il quiz si sblocca con il tuo Tema Natale.</h1>
      <p className="text-[#2A1F14]/60 leading-relaxed mb-10">Le 30 domande — dalla più semplice alla più avanzata — sono generate da ARIES a partire dal tuo cielo di nascita. Calcola prima il tema, poi torna qui.</p>
      <Link to="/tema-natale" data-testid="quiz-go-natal" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gold text-black text-sm font-medium hover:bg-[#D4A056] transition-colors">Calcola il mio Tema Natale <ArrowRight size={16} /></Link>
    </div>
  );
}

function Preparing({ level }) {
  return (
    <div className="text-center py-24" data-testid="quiz-preparing">
      <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 12, ease: "linear" }} className="w-28 h-28 rounded-full border border-gold/40 flex items-center justify-center mx-auto mb-8"><Star size={20} className="text-gold" /></motion.div>
      <p className="font-display text-2xl text-[#2A1F14]">ARIES sta scrivendo le domande del livello {level}…</p>
      <p className="text-[#2A1F14]/45 text-sm mt-3">Personalizzate sul tuo cielo. Pochi istanti.</p>
    </div>
  );
}

function ScoreRing({ value }) {
  const r = 54, c = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 128 128" className="w-40 h-40" data-testid="quiz-score-ring">
      <circle cx="64" cy="64" r={r} fill="none" stroke="rgba(42,31,20,0.1)" strokeWidth="6" />
      <motion.circle cx="64" cy="64" r={r} fill="none" stroke="#B9863F" strokeWidth="6" strokeLinecap="round" transform="rotate(-90 64 64)"
        initial={{ strokeDasharray: c, strokeDashoffset: c }} animate={{ strokeDashoffset: c - (c * value) / 100 }} transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }} />
      <text x="64" y="60" textAnchor="middle" fontSize="30" fill="#2A1F14" fontFamily="Bodoni Moda, serif">{value}</text>
      <text x="64" y="80" textAnchor="middle" fontSize="10" fill="#B9863F" letterSpacing="2">SU 100</text>
    </svg>
  );
}

function Results({ quiz, chart }) {
  const sc = quiz.score, ev = quiz.evaluation;
  return (
    <div className="space-y-16" data-testid="quiz-results">
      <Reveal className="grid lg:grid-cols-[auto_1fr] gap-10 items-center">
        <ScoreRing value={sc.total} />
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-gold mb-3">La tua valutazione</p>
          <h1 className="font-display text-5xl sm:text-6xl text-[#2A1F14] leading-none mb-4" data-testid="quiz-grade">{sc.grade}</h1>
          <p className="text-[#2A1F14]/65 text-lg font-light">{sc.grade_desc}</p>
          <div className="flex flex-wrap gap-3 mt-6">
            {Object.entries(sc.by_level).map(([k, v]) => <span key={k} className="text-xs px-3 py-1.5 rounded-full border border-[#2A1F14]/15 text-[#2A1F14]/70">{k}: <b className="text-[#2A1F14]">{v.points}</b>/{v.max}</span>)}
          </div>
        </div>
      </Reveal>

      <Reveal className="grid md:grid-cols-2 gap-10">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-gold mb-5">Per area</p>
          <div className="space-y-4" data-testid="quiz-areas">
            {sc.by_area.filter((a) => a.total).map((a) => (
              <div key={a.area}>
                <div className="flex justify-between text-sm mb-1"><span className="text-[#2A1F14]/80">{a.area}</span><span className="text-[#2A1F14]/50">{a.correct}/{a.total}</span></div>
                <div className="h-1.5 rounded-full bg-[#2A1F14]/8 overflow-hidden"><div className="h-full bg-gold rounded-full" style={{ width: `${(a.correct / a.total) * 100}%` }} /></div>
              </div>
            ))}
          </div>
        </div>
        {ev && (
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-gold mb-5">La lettura di ARIES</p>
            <p className="text-[#2A1F14]/80 leading-relaxed mb-6" data-testid="quiz-synthesis">{ev.sintesi}</p>
            <div className="grid sm:grid-cols-2 gap-6 text-sm">
              <div><p className="text-[#2A1F14] mb-2 font-medium">Punti di forza</p><ul className="space-y-1.5">{(ev.punti_forza || []).map((p, i) => <li key={i} className="flex gap-2 text-[#2A1F14]/70"><Check size={14} className="text-gold mt-0.5 shrink-0" />{p}</li>)}</ul></div>
              <div><p className="text-[#2A1F14] mb-2 font-medium">Aree di crescita</p><ul className="space-y-1.5">{(ev.aree_crescita || []).map((p, i) => <li key={i} className="flex gap-2 text-[#2A1F14]/70"><ArrowRight size={14} className="text-gold mt-0.5 shrink-0" />{p}</li>)}</ul></div>
            </div>
          </div>
        )}
      </Reveal>

      {ev && (
        <Reveal><div className="glass rounded-3xl p-8 sm:p-12 constellation" data-testid="quiz-path">
          <div className="inline-flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] mb-4"><Sparkles size={14} /> Il tuo percorso consigliato</div>
          <h2 className="font-display text-3xl sm:text-4xl text-[#2A1F14] mb-8">Da qui, il passo successivo.</h2>
          <div className="grid md:grid-cols-2 gap-6 mb-10">
            {(ev.corsi || []).map((c, i) => (
              <div key={i} className="border border-[#2A1F14]/10 rounded-2xl p-6 bg-[#F7EFE0]/60" data-testid="quiz-course">
                <GraduationCap size={18} className="text-gold mb-3" />
                <h3 className="font-display text-2xl text-[#2A1F14] mb-2">{c.titolo}</h3>
                <p className="text-[#2A1F14]/65 text-sm leading-relaxed mb-5">{c.motivo}</p>
                <div className="flex flex-wrap gap-3">
                  <BookingDialog title={`Iscrizione — ${c.titolo}`} endpoint="enrollments" source="quiz" presets={{ course: c.titolo, name: chart?.name || "" }}
                    trigger={<button data-testid="quiz-enroll" className="px-5 py-2.5 rounded-full bg-gold text-black text-sm font-medium hover:bg-[#D4A056] transition-colors">Iscriviti</button>} />
                  {c.slug && <Link to={`/academy/${c.slug}`} className="px-5 py-2.5 rounded-full border border-[#2A1F14]/15 text-[#2A1F14]/70 text-sm hover:border-gold hover:text-gold transition-colors">Scopri il corso</Link>}
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-[#2A1F14]/10 pt-8 grid lg:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <p className="text-[#2A1F14] font-medium mb-1">La tua sessione personalizzata, tarata su questo risultato</p>
              <p className="text-[#2A1F14]/65 text-sm leading-relaxed">{ev.sessione_focus}</p>
            </div>
            <BookingDialog title={`Prenota — ${SESSION_SERVICE}`} source="quiz" presets={{ type: "servizio", service: SESSION_SERVICE, name: chart?.name || "", message: `Quiz spirituale: ${sc.total}/100 (${sc.grade}). Tema natale: ${window.location.origin}/tema-natale?id=${quiz.chart_id}` }}
              trigger={<button data-testid="quiz-book-session" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#2A1F14] text-[#F7EFE0] text-sm font-medium hover:bg-gold hover:text-black transition-colors whitespace-nowrap"><CalendarCheck size={16} /> Prenota la sessione</button>} />
          </div>
        </div></Reveal>
      )}

      <div className="flex flex-wrap gap-5 justify-center text-sm">
        <a href={`${API}/natal-chart/${quiz.chart_id}/pdf`} target="_blank" rel="noreferrer" onClick={() => track("natal_pdf")} data-testid="quiz-pdf" className="inline-flex items-center gap-2 text-[#2A1F14]/60 hover:text-gold transition-colors"><FileDown size={15} /> Scarica tema + valutazione in PDF</a>
        <Link to={`/tema-natale?id=${quiz.chart_id}`} data-testid="quiz-back-natal" className="inline-flex items-center gap-2 text-[#2A1F14]/60 hover:text-gold transition-colors"><Star size={15} /> Torna al tuo tema</Link>
      </div>
    </div>
  );
}

export default function Quiz() {
  const [params] = useSearchParams();
  const chartId = params.get("chart");
  const [chart, setChart] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(!!chartId);
  const [feedback, setFeedback] = useState(null); // { correct, correct_index, explanation }
  const [chosen, setChosen] = useState(null);
  const [completing, setCompleting] = useState(false);
  const pollRef = useRef(null);

  const refresh = useCallback(() => quiz && api.get(`/quiz/${quiz.id}`).then((r) => setQuiz(r.data)).catch(() => {}), [quiz]);

  useEffect(() => {
    if (!chartId) return;
    api.get(`/natal-chart/${chartId}`).then((r) => setChart(r.data)).catch(() => {});
    api.get(`/quiz/by-chart/${chartId}`).then(async (r) => {
      if (r.data && r.data.levels["1"]) { setQuiz(r.data); setLoading(false); return; }
      track("quiz_start");
      const res = await api.post("/quiz/start", { chart_id: chartId });
      setQuiz(res.data);
    }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail))).finally(() => setLoading(false));
  }, [chartId]);

  const questions = quiz ? LEVELS.flatMap((l) => quiz.levels[String(l.n)] || []) : [];
  const current = questions.find((q) => q.chosen === undefined);
  const currentLevel = current ? current.level : quiz ? Math.min(3, (Object.values(quiz.levels).filter(Boolean).length) + 1) : 1;
  const levelReady = quiz && quiz.levels[String(currentLevel)];
  const allAnswered = quiz && Object.values(quiz.levels).every(Boolean) && !current;

  useEffect(() => {
    clearInterval(pollRef.current);
    if (quiz && !levelReady && quiz.status === "in_corso" && currentLevel <= 3) pollRef.current = setInterval(refresh, 4000);
    return () => clearInterval(pollRef.current);
  }, [quiz, levelReady, currentLevel, refresh]);

  useEffect(() => {
    if (allAnswered && quiz.status === "in_corso" && !completing) {
      setCompleting(true);
      api.post(`/quiz/${quiz.id}/complete`).then((r) => { setQuiz(r.data); track("quiz_complete"); }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail))).finally(() => setCompleting(false));
    }
  }, [allAnswered, quiz, completing]);

  const answer = async (idx) => {
    if (chosen !== null || !current) return;
    setChosen(idx);
    try {
      const { data } = await api.post(`/quiz/${quiz.id}/answer`, { question_id: current.id, choice: idx });
      setFeedback(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setChosen(null); }
  };
  const next = () => {
    setQuiz((q) => ({ ...q, levels: Object.fromEntries(Object.entries(q.levels).map(([k, v]) => [k, v && v.map((x) => x.id === current.id ? { ...x, chosen, correct: feedback.correct_index, explanation: feedback.explanation } : x)])), answered: q.answered + 1 }));
    setChosen(null); setFeedback(null);
  };

  const answeredCount = questions.filter((q) => q.chosen !== undefined).length;
  const idxInLevel = current ? (quiz.levels[String(current.level)] || []).findIndex((q) => q.id === current.id) + 1 : 0;

  return (
    <div className="pt-32 pb-28 min-h-screen constellation">
      <Seo title="Quiz Spirituale · 30 domande sul tuo cielo" description="Un quiz spirituale personalizzato sul tuo tema natale: 30 domande in 3 livelli, valutazione con voto e percorso consigliato in Lux Academy." noindex={!!chartId} />
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
        {!chartId ? <Locked /> : loading ? <Preparing level={1} /> : !quiz ? <Locked /> : quiz.status === "completato" && quiz.score ? <Results quiz={quiz} chart={chart} /> : completing || allAnswered ? (
          <div className="text-center py-24" data-testid="quiz-evaluating"><motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 12, ease: "linear" }} className="w-28 h-28 rounded-full border border-gold/40 flex items-center justify-center mx-auto mb-8"><Sparkles size={20} className="text-gold" /></motion.div><p className="font-display text-2xl text-[#2A1F14]">ARIES sta componendo la tua valutazione…</p></div>
        ) : (
          <>
            <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-gold mb-3">Quiz Spirituale{chart ? ` · il cielo di ${chart.name}` : ""}</p>
                <h1 className="font-display text-4xl sm:text-5xl text-[#2A1F14] leading-tight">Trenta domande, un solo cielo: il tuo.</h1>
              </div>
              <div className="text-right"><p className="font-display text-4xl text-gold" data-testid="quiz-progress">{answeredCount}<span className="text-[#2A1F14]/30 text-2xl">/30</span></p><p className="text-[#2A1F14]/45 text-xs uppercase tracking-wider">risposte</p></div>
            </div>
            <div className="flex gap-2 mb-12" data-testid="quiz-levels">
              {LEVELS.map((l) => {
                const done = (quiz.levels[String(l.n)] || []).every((q) => q.chosen !== undefined) && quiz.levels[String(l.n)];
                const active = l.n === currentLevel;
                return (
                  <div key={l.n} className={`flex-1 rounded-2xl border px-4 py-3 text-sm flex items-center justify-between ${active ? "border-gold bg-gold/10" : "border-[#2A1F14]/10"}`} data-testid={`quiz-level-${l.n}`}>
                    <span className={active ? "text-[#2A1F14]" : "text-[#2A1F14]/50"}>{l.n}. {l.name} <span className="text-xs opacity-60">· {l.pts} pt</span></span>
                    {done ? <Check size={15} className="text-gold" /> : l.n > currentLevel ? <Lock size={14} className="text-[#2A1F14]/30" /> : null}
                  </div>
                );
              })}
            </div>

            {!levelReady ? <Preparing level={currentLevel} /> : current && (
              <AnimatePresence mode="wait">
                <motion.div key={current.id} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }} className="glass rounded-3xl p-8 sm:p-12" data-testid="quiz-question">
                  <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em] text-gold mb-6"><span>{current.area}</span><span>Domanda {idxInLevel} / 10 · {LEVELS[current.level - 1].name}</span></div>
                  <h2 className="font-display text-2xl sm:text-3xl text-[#2A1F14] leading-snug mb-10" data-testid="quiz-question-text">{current.question}</h2>
                  <div className="grid gap-3">
                    {current.options.map((o, i) => {
                      const isChosen = chosen === i, isCorrect = feedback && feedback.correct_index === i;
                      const cls = feedback ? (isCorrect ? "border-emerald-500/60 bg-emerald-500/10" : isChosen ? "border-rose-400/60 bg-rose-400/10" : "border-[#2A1F14]/10 opacity-60") : isChosen ? "border-gold bg-gold/10" : "border-[#2A1F14]/12 hover:border-gold hover:bg-gold/5";
                      return (
                        <button key={i} onClick={() => answer(i)} disabled={chosen !== null} data-testid={`quiz-option-${i}`} className={`text-left rounded-2xl border px-5 py-4 flex items-start gap-4 transition-colors ${cls}`}>
                          <span className="font-display text-gold text-lg w-6 shrink-0">{LETTERS[i]}</span>
                          <span className="text-[#2A1F14]/85 text-sm sm:text-base leading-relaxed flex-1">{o}</span>
                          {feedback && isCorrect && <Check size={18} className="text-emerald-600 shrink-0 mt-1" />}
                          {feedback && isChosen && !isCorrect && <X size={18} className="text-rose-500 shrink-0 mt-1" />}
                        </button>
                      );
                    })}
                  </div>
                  <AnimatePresence>
                    {feedback && (
                      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-8 border-t border-[#2A1F14]/10 pt-6" data-testid="quiz-feedback">
                        <p className={`text-sm font-medium mb-2 ${feedback.correct ? "text-emerald-700" : "text-rose-600"}`}>{feedback.correct ? `Esatto · +${LEVELS[current.level - 1].pts} punti` : "Non era questa — ma ora lo sai."}</p>
                        <p className="text-[#2A1F14]/70 text-sm leading-relaxed mb-6">{feedback.explanation}</p>
                        <button onClick={next} data-testid="quiz-next" className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-gold text-black text-sm font-medium hover:bg-[#D4A056] transition-colors">{answeredCount + 1 >= 30 ? "Vai alla valutazione" : idxInLevel === 10 ? "Livello completato · avanti" : "Prossima domanda"} <ArrowRight size={16} /></button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              </AnimatePresence>
            )}
          </>
        )}
      </div>
    </div>
  );
}
